import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react'
import type { UserPreferences } from 'paintscout'
import type { Theme } from '@material-ui/core'
import { makeStyles } from '@material-ui/core'
import type { OverridableValue } from '@paintscout/util/builder'
import type { InputFieldProps } from '@paintscout/ui/components/InputField'
import type { StyleClasses } from '@paintscout/ui/styles'
import Typography from '@paintscout/ui/components/Typography'
import KeypadInputField from '../KeypadInputField'

import ResetAdornment from './ResetAdornment'

const useStyles = makeStyles<Theme, OverridableInputFieldProps>((theme) => ({
  root: {
    '& p': {
      marginTop: '0 !important'
    }
  },
  inputFocused: {
    '& $adornment': {
      color: theme.palette.primary.main
    }
  },
  icon: {},
  adornment: {
    marginLeft: theme.typography.pxToRem(7),
    marginRight: theme.typography.pxToRem(4),
    color: theme.paintscout.palette.brand.Neutral.P70
  },
  inputWrapper: {},
  helperText: {},
  helperTextContainer: {
    // position: 'absolute',
    marginTop: 3
  },
  inputInput: {}
}))

export interface OverridableInputFieldProps extends Omit<InputFieldProps, 'value' | 'onChange' | 'classes'> {
  classes?: StyleClasses<typeof useStyles>
  value?: OverridableValue
  helpPrefix?: string
  helpText?: string
  hideHelp?: boolean
  resetBlank?: boolean
  flipX?: boolean
  preferences?: UserPreferences
  onChange?: (value: OverridableValue) => void
  showKeypad?: 'always' | 'touch' | 'never'
  allowNegative?: boolean
}

const OverridableInputField = forwardRef((props: OverridableInputFieldProps, ref) => {
  const classes = useStyles(props)
  const {
    value,
    resetBlank,
    helpPrefix = 'Default Value:',
    hideHelp,
    helpText,
    format,
    onChange,
    flipX,
    allowNegative,

    ...baseInputProps
  } = props

  function getValue(value: OverridableValue): string {
    return (Math.round((value.useCustom ? value.custom : value.default) * 100) / 100).toString()
  }

  const disabled = !!baseInputProps?.disabled
  const [rawValue, setRawValue] = useState(getValue(value))
  const [currentValue, setCurrentValue] = useState(value)
  // Tracks whether the user is actively editing this field. While editing, we must not let prop-driven
  // value updates (e.g. debounced quote recalculations) overwrite the in-progress input - doing so makes
  // deleting digits "sticky" (see #20848). We re-sync from props on blur instead.
  const isEditingRef = useRef(false)
  // Closure-proof mirror of the most-recently-committed value. handleChange writes it synchronously on
  // every edit; handleBlur reads it. This is required because on the keypad "Done" path
  // (Keypad.handleClose) onChange and the synthesized onBlur fire synchronously before a re-render, so
  // the `value` prop AND the `currentValue`/`rawValue` state captured in the blur closure are all still
  // pre-edit. Reverting to any of them on blur discarded the user's entry (#21630 keypad hours, #21625
  // ref-based group price). A ref is the only thing that reflects the edit across that synchronous gap.
  // Initialized to the mount-time prop; the `[value]` effect below keeps it in sync while not editing,
  // and commitValue() updates it on every edit, so it always trails the latest committed value.
  const latestCommittedRef = useRef(value)

  useEffect(() => {
    // While the user is mid-edit, leave both the display string (rawValue) and the committed value
    // (currentValue, read by consumers via the imperative getValue()) untouched so a debounced prop
    // update can't clobber the in-progress edit. handleBlur re-syncs once editing ends.
    if (!isEditingRef.current) {
      setRawValue(getValue(value))
      commitValue(value)
    }
  }, [value])

  useImperativeHandle(ref, () => ({
    getValue: () => {
      return latestCommittedRef.current
    },
    setValue: (value: OverridableValue) => {
      commitValue(value)
    }
  }))

  // Commit a value to state and the closure-proof ref together. Keeping these two writes in one place
  // makes it impossible for a write path to update one without the other. onChange is fired separately
  // by the edit handlers that need it (not here) so imperative setValue()/prop sync don't re-emit it.
  function commitValue(v: OverridableValue) {
    setCurrentValue(v)
    latestCommittedRef.current = v
  }

  let helperText = null
  if (!hideHelp && helpText) {
    helperText = <>{helpText}</>
  } else if (!hideHelp && currentValue.useCustom && (currentValue.default || currentValue.default === 0)) {
    helperText = (
      <div className={classes.helperTextContainer}>
        {helpPrefix}{' '}
        <Typography
          display="inline"
          variant={'subtitle2'}
          component={'span'}
          format={format}
          classes={{ root: classes.helperText }}
          value={currentValue.default === 0 ? '0' : currentValue.default}
        />
      </div>
    )
  }

  return (
    <>
      <KeypadInputField
        {...baseInputProps}
        classes={{
          root: classes.root,
          adornment: classes.adornment,
          icon: classes.icon,
          inputWrapper: classes.inputWrapper,
          inputFocused: classes.inputFocused,
          inputInput: classes.inputInput
        }}
        value={!rawValue ? '' : rawValue}
        format={format}
        helperText={helperText}
        onChange={handleChange}
        onBlur={handleBlur}
        flipX={flipX}
        allowNegative={allowNegative}
        endAdornment={currentValue.useCustom && !disabled ? <ResetAdornment onResetClick={handleResetValue} /> : null}
      />
    </>
  )

  function handleResetValue() {
    const updatedValue = {
      ...value,
      useCustom: false
    }

    setRawValue(value.default)
    onChange?.(updatedValue)
    commitValue(updatedValue)
  }

  function handleBlur() {
    isEditingRef.current = false
    if (resetBlank && rawValue === '') {
      const updatedValue = { ...value, useCustom: false }
      setRawValue(value.default)
      onChange?.(updatedValue)
      commitValue(updatedValue)
    } else {
      // Editing finished: settle the input on the value the user actually committed (tracked in
      // latestCommittedRef), NOT the `value` prop. On the keypad "Done" path onChange + this blur fire
      // synchronously before any re-render, so the `value` prop captured here is still pre-edit;
      // reverting to it dropped the entry (#21630/#21625). Genuine external/recalculated prop changes
      // still reconcile through the `[value]` effect once editing has ended.
      const committed = latestCommittedRef.current
      setRawValue(getValue(committed))
      setCurrentValue(committed)
    }
  }

  function handleChange(event: React.ChangeEvent<HTMLInputElement>) {
    isEditingRef.current = true
    const previousValue = value.useCustom ? value.custom : value.default

    const newRawValue = event.target.value
    setRawValue(newRawValue)

    if ((newRawValue.slice(-1) === '.' && newRawValue.length === 1) || newRawValue === '') {
      return null
    }

    const newValue = parseFloat(newRawValue)
    const defaultValue = typeof value.default === 'string' ? parseFloat(value.default) : value.default

    if (value.useCustom && newValue === Number(Number(defaultValue).toFixed(2))) {
      const updatedValue = { ...value, useCustom: false }
      onChange?.(updatedValue)
      commitValue(updatedValue)
    } else if (previousValue === newValue) {
      return null
    } else if (((value as any) !== '' && resetBlank) || (value as any) === '') {
      const updatedValue = { ...value, useCustom: true, custom: newValue }
      onChange?.(updatedValue)
      commitValue(updatedValue)
    }
  }
})
OverridableInputField.displayName = 'OverridableInputField'

export default OverridableInputField
